import React, { useEffect, useState, useCallback } from 'react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { attendanceAPI } from '../services/api';
import {
  Calendar,
  Clock,
  Filter,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  LogIn,
  LogOut,
  Timer
} from 'lucide-react';
import { getCurrentNepalTime, formatNepalTime } from '../utils/timeUtils';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

const MyAttendance = () => {
  const [attendance, setAttendance] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: format(startOfMonth(getCurrentNepalTime()), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(getCurrentNepalTime()), 'yyyy-MM-dd'),
  });

  // Stats
  const [stats, setStats] = useState({
    totalDays: 0,
    presentDays: 0,
    lateDays: 0,
    avgHours: 0
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await attendanceAPI.getMyAttendance(dateRange.startDate, dateRange.endDate);
      const data = res.data || [];
      setAttendance(data);

      // Calculate stats
      const present = data.filter(r => r.status === 'present').length;
      const late = data.filter(r => r.status === 'late').length;
      const totalHours = data.reduce((sum, r) => sum + (r.workingHours || 0), 0);

      setStats({
        totalDays: data.length,
        presentDays: present,
        lateDays: late,
        avgHours: data.length > 0 ? (totalHours / data.length).toFixed(1) : 0
      });
    } catch (err) {
      toast.error('Failed to load attendance');
      setAttendance([]);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'present':
        return { icon: CheckCircle, color: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-200', label: 'Present' };
      case 'late':
        return { icon: AlertCircle, color: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-200', label: 'Late' };
      case 'absent':
        return { icon: XCircle, color: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-200', label: 'Absent' };
      default:
        return { icon: Clock, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', label: status };
    }
  };

  return (
    <div className="">
      <div className=" space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-500" />
              My Attendance
            </h1>
            <p className="text-sm text-slate-500 mt-1">Track your attendance history and performance</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-md p-4 shadow-sm border border-slate-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <Calendar size={20} className="text-gray-700" />
              </div>
              <div>
                <div className="text-lg font-semibold text-slate-800">{stats.totalDays}</div>
                <div className="text-xs text-slate-500">Total Days</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-md p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <CheckCircle size={20} className="text-gray-700" />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">{stats.presentDays}</div>
                <div className="text-xs text-slate-500">Present</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-md p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <AlertCircle size={20} className="text-gray-700" />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">{stats.lateDays}</div>
                <div className="text-xs text-slate-500">Late</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-md p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <Timer size={20} className="text-gray-700" />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">{stats.avgHours}h</div>
                <div className="text-xs text-slate-500">Avg Hours</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filter Card */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-end">
            <div className="flex-1 grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleChange}
                  className="w-full min-w-0 px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleChange}
                  className="w-full min-w-0 px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <button
              onClick={fetchData}
              disabled={isLoading}
              className="w-full sm:w-auto px-6 py-2.5 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              <Filter size={16} />
              {isLoading ? 'Loading...' : 'Apply'}
            </button>
          </div>
        </div>

        {/* Attendance List */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
            <div className="flex items-center gap-2">
              <TrendingUp size={18} className="text-gray-700" />
              <h3 className="text-sm font-semibold text-gray-900">Attendance Records</h3>
            </div>
            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
              {attendance.length} records
            </span>
          </div>

          {isLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-200 mx-auto"></div>
              <p className="mt-3 text-slate-500 text-sm">Loading attendance...</p>
            </div>
          ) : attendance.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-slate-700 font-semibold">No records found</h3>
              <p className="text-slate-400 text-sm mt-1">Try adjusting your date range</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {attendance.map((rec, index) => {
                const statusConfig = getStatusConfig(rec.status);
                const StatusIcon = statusConfig.icon;

                return (
                  <motion.div
                    key={rec._id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="px-4 py-3 hover:bg-slate-50/50 transition-colors"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      {/* Left: Date & Status */}
                      <div className="flex min-w-0 flex-wrap items-center gap-3">
                        <div className="text-center min-w-[44px]">
                          <div className="text-lg font-semibold text-slate-800">
                            {format(new Date(rec.date), 'd')}
                          </div>
                          <div className="text-[10px] font-semibold text-slate-400 uppercase">
                            {format(new Date(rec.date), 'MMM')}
                          </div>
                        </div>

                        <div className={`px-3 py-1.5 rounded-lg ${statusConfig.bg} ${statusConfig.border} border flex items-center gap-1.5`}>
                          <StatusIcon size={14} className={statusConfig.color} />
                          <span className={`text-xs font-bold ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </div>

                        {rec.isHolidayWork && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-[10px] font-bold rounded-lg border border-gray-200">
                            Holiday Work
                          </span>
                        )}
                      </div>

                      {/* Right: Times & Duration */}
                      <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-6">
                        <div className="rounded-md bg-slate-50 px-2 py-1.5 text-center sm:bg-transparent sm:p-0">
                          <div className="flex items-center justify-center gap-1 text-gray-700">
                            <LogIn size={12} />
                            <span className="text-sm font-bold">
                              {rec.checkInTime ? formatNepalTime(rec.checkInTime, 'HH:mm') : '—'}
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-400 uppercase">In</div>
                        </div>

                        <div className="rounded-md bg-slate-50 px-2 py-1.5 text-center sm:bg-transparent sm:p-0">
                          <div className="flex items-center justify-center gap-1 text-gray-700">
                            <LogOut size={12} />
                            <span className="text-sm font-bold">
                              {rec.checkOutTime ? formatNepalTime(rec.checkOutTime, 'HH:mm') : '—'}
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-400 uppercase">Out</div>
                        </div>

                        <div className="rounded-md bg-slate-50 px-2 py-1.5 text-center min-w-0 sm:min-w-[60px] sm:bg-transparent sm:p-0">
                          <div className="text-sm font-semibold text-gray-700">
                            {typeof rec.workingHours === 'number' ? rec.workingHours.toFixed(1) : '0.0'}
                          </div>
                          <div className="text-[10px] text-slate-400 uppercase">Hours</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyAttendance;
